import { z } from 'zod';
import { isCalendarDate } from '../check-ins/schema';
import { apiEnvelope } from '../envelope';
import {
  moduleCompletionSourceSchema,
  moduleKeySchema,
  moduleProgressStatusSchema,
} from './module-catalogue';

// ─── module progress wire contract ──────────────────────────────────────────
//
// What the member app and Atlas read and send for a member's progress through
// a CBT module. Requests are strict: a writer field such as `recordedBy` in a
// body is refused rather than ignored, because the writer is always the
// verified token subject. Responses are non-strict so a client keeps parsing
// when the server adds a field (ADR-135).

/** Refusal codes a module progress route answers with. */
export const MODULE_PROGRESS_ERROR_CODES = {
  moduleNotFound: 'module_not_found',
  invalidStepKey: 'invalid_step_key',
  invalidCompletedOn: 'invalid_completed_on',
  invalidVoidReason: 'invalid_void_reason',
  moduleAlreadyComplete: 'module_already_complete',
  completionNotFound: 'completion_not_found',
  completionAlreadyVoided: 'completion_already_voided',
  invalidResetReason: 'invalid_reset_reason',
  moduleNotStarted: 'module_not_started',
  moduleResetUnavailable: 'module_reset_unavailable',
  completionNotInCurrentCycle: 'completion_not_in_current_cycle',
} as const;
export type ModuleProgressErrorCode =
  (typeof MODULE_PROGRESS_ERROR_CODES)[keyof typeof MODULE_PROGRESS_ERROR_CODES];

/** Longest staff note a completion keeps; the table CHECK holds the same bound. */
export const MODULE_COMPLETION_NOTE_MAX_LENGTH = 2000;
/** Longest void reason; the table CHECK holds the same bound. */
export const MODULE_COMPLETION_VOID_REASON_MAX_LENGTH = 1000;
/** Longest reset reason; the table CHECK holds the same bound. */
export const MODULE_RESET_REASON_MAX_LENGTH = 1000;

/** A member-local calendar day (`YYYY-MM-DD`, ADR-095), never an instant. */
export const moduleCompletionDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD local date')
  .refine(isCalendarDate, 'must be a real calendar date');

// ─── requests ───────────────────────────────────────────────────────────────

/** A staff member recording that the member completed a module on a day. */
export const staffRecordModuleCompletionRequestSchema = z
  .object({
    completedOn: moduleCompletionDaySchema,
    note: z.string().trim().min(1).max(MODULE_COMPLETION_NOTE_MAX_LENGTH).optional(),
  })
  .strict();
export type StaffRecordModuleCompletionRequest = z.infer<
  typeof staffRecordModuleCompletionRequestSchema
>;

/** A staff member voiding a completion recorded in error. */
export const staffVoidModuleCompletionRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(MODULE_COMPLETION_VOID_REASON_MAX_LENGTH),
  })
  .strict();
export type StaffVoidModuleCompletionRequest = z.infer<
  typeof staffVoidModuleCompletionRequestSchema
>;

/**
 * A staff member resetting a module, which opens a new cycle: earlier
 * completions and steps stop counting and the member completes it again.
 */
export const staffResetModuleRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(MODULE_RESET_REASON_MAX_LENGTH),
  })
  .strict();
export type StaffResetModuleRequest = z.infer<typeof staffResetModuleRequestSchema>;

// ─── member view ────────────────────────────────────────────────────────────

/**
 * The member's own view: the effective completion's date and source, never
 * who recorded it, its note or the void history.
 */
export const memberModuleProgressSchema = z.object({
  moduleKey: z.string(),
  status: moduleProgressStatusSchema,
  stepsDone: z.number().int().nonnegative(),
  stepsTotal: z.number().int().nonnegative(),
  nextStepKey: z.string().nullable(),
  steps: z.array(z.object({ stepKey: z.string(), done: z.boolean() })),
  completion: z
    .object({
      completedOn: moduleCompletionDaySchema,
      completedAt: z.string().nullable(),
      source: moduleCompletionSourceSchema,
    })
    .nullable(),
});
export type MemberModuleProgress = z.infer<typeof memberModuleProgressSchema>;

/** The member `GET …/progress` and step `POST` response envelope (ADR-002). */
export const memberModuleProgressResponseSchema = apiEnvelope(memberModuleProgressSchema);
export type MemberModuleProgressResponse = z.infer<typeof memberModuleProgressResponseSchema>;

// ─── staff view ─────────────────────────────────────────────────────────────

const staffCompletionSchema = z.object({
  id: z.string().uuid(),
  completedOn: moduleCompletionDaySchema,
  completedAt: z.string().nullable(),
  source: moduleCompletionSourceSchema,
  recordedBy: z.string(),
  /** The staff recorder's display name; null when it does not resolve, and for `app` and `legacy_1_0`. */
  recordedByName: z.string().nullable(),
  note: z.string().nullable(),
  /** The reset that opened the completion's cycle; null for the first cycle. */
  resetId: z.string().uuid().nullable(),
});

/**
 * The care-team view: the current cycle's status and effective completion with
 * who recorded it, plus every completion of every cycle newest first with its
 * void, and every reset newest first, so Atlas can show who changed what.
 */
export const staffModuleProgressSchema = z.object({
  moduleKey: z.string(),
  status: moduleProgressStatusSchema,
  stepsDone: z.number().int().nonnegative(),
  stepsTotal: z.number().int().nonnegative(),
  completion: staffCompletionSchema.nullable(),
  /** When the current cycle's reset was made; null for the first cycle. */
  cycleStartedAt: z.string().nullable(),
  /**
   * The member-local day the current cycle started on, in the zone the member
   * was in at the reset (ADR-095 Rule 10); null for the first cycle. A staff
   * record may not be dated before it.
   */
  cycleStartedOn: moduleCompletionDaySchema.nullable(),
  /** The reset that opened the current cycle; null for the first cycle. */
  cycleResetId: z.string().uuid().nullable(),
  /** Whether the reset route would accept a reset now: resets on and something to reset. */
  resetAvailable: z.boolean(),
  resets: z.array(
    z.object({
      id: z.string().uuid(),
      resetAt: z.string(),
      resetBy: z.string(),
      resetByName: z.string().nullable(),
      reason: z.string(),
    }),
  ),
  history: z.array(
    staffCompletionSchema.extend({
      createdAt: z.string(),
      inCurrentCycle: z.boolean(),
      void: z
        .object({
          voidedAt: z.string(),
          voidedBy: z.string(),
          voidedByName: z.string().nullable(),
          reason: z.string(),
        })
        .nullable(),
    }),
  ),
});
export type StaffModuleProgress = z.infer<typeof staffModuleProgressSchema>;

export const staffModuleProgressResponseSchema = apiEnvelope(staffModuleProgressSchema);
export type StaffModuleProgressResponse = z.infer<typeof staffModuleProgressResponseSchema>;

// ─── domain event payloads ──────────────────────────────────────────────────
//
// Strict: the producer parses before it enqueues, so a note, a reason or a
// staff id leaking in fails the parse instead of reaching the bus. The source
// travels as `completionSource`: the analytics forwarder bounds a lifted value
// by its data key across every event, and stamps its own `source` on capture.

/** `member.module.completed`: a completion was recorded by any path but the import. */
export const memberModuleCompletedPayloadSchema = z
  .object({
    completionId: z.string().uuid(),
    moduleKey: moduleKeySchema,
    completedOn: moduleCompletionDaySchema,
    completionSource: moduleCompletionSourceSchema,
    // Injected by buildAnnotatedDomainEvent from the analytics catalogue.
    status: z.literal('recorded').optional(),
  })
  .strict();
export type MemberModuleCompletedPayload = z.infer<typeof memberModuleCompletedPayloadSchema>;

/**
 * `member.module.completion.voided`: a completion was voided.
 * `isEffectiveStatusComplete` is the module's status after the void, so a
 * consumer never has to re-derive whether another completion took over.
 */
export const memberModuleCompletionVoidedPayloadSchema = z
  .object({
    completionId: z.string().uuid(),
    moduleKey: moduleKeySchema,
    completionSource: moduleCompletionSourceSchema,
    isEffectiveStatusComplete: z.boolean(),
    status: z.literal('revoked').optional(),
  })
  .strict();
export type MemberModuleCompletionVoidedPayload = z.infer<
  typeof memberModuleCompletionVoidedPayloadSchema
>;

/**
 * `member.module.reset`: staff opened a new cycle of the module. The reset id
 * is the event id. `priorModuleStatus` is the cycle's status just before the
 * reset, which is never `not_started` (there is nothing to reset). No reason
 * and no staff identity.
 */
export const memberModuleResetPayloadSchema = z
  .object({
    resetId: z.string().uuid(),
    moduleKey: moduleKeySchema,
    priorModuleStatus: z.enum(['in_progress', 'complete']),
    status: z.literal('restarted').optional(),
  })
  .strict();
export type MemberModuleResetPayload = z.infer<typeof memberModuleResetPayloadSchema>;
